import React, { useState, useCallback, useEffect } from 'react';
import { TbBottle } from 'react-icons/tb';
import { Outlet, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfileImage, DEFAULT_AVATAR_URL } from '../context/ProfileImageContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { BiGridAlt, BiUser, BiPackage, BiShoppingBag, BiLogOut } from 'react-icons/bi';

const Dashboard = ({ activeView, setActiveView }) => {
  const { user, logout, parentGroupLabel } = useAuth();
  const { imageUrl: profileImageUrl } = useProfileImage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Automatically update active view from URL
  useEffect(() => {
    if (matchPath('/dashboard/products/:productId', location.pathname)) {
      setActiveView('products');
    } else if (matchPath('/dashboard/products', location.pathname)) {
      setActiveView('products');
    } else if (matchPath('/dashboard/container-types/:id', location.pathname)) {
      setActiveView('containerTypes');
    } else if (matchPath('/dashboard/container-types', location.pathname)) {
      setActiveView('containerTypes');
    } else if (matchPath('/dashboard/users', location.pathname)) {
      setActiveView('userManagement');
    } else if (matchPath('/dashboard/inventory/:productUuid', location.pathname)) {
      setActiveView('inventory');
    } else if (matchPath('/dashboard/inventory', location.pathname)) {
      setActiveView('inventory');
    } else if (matchPath('/dashboard/profile', location.pathname)) {
      setActiveView('profile');
    } else {
      setActiveView('dashboard');
    }
  }, [location.pathname, setActiveView]);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout]);

  return (
    <Container fluid className="px-0 dashboard-container">
      <Row className="bg-light py-2 mx-0 border-bottom">
        <Col xs="auto" className="d-flex align-items-center">
          <span className="fw-bold">Welcome, {user?.name || 'User'}</span>
          {parentGroupLabel && (
            <span
              className="ms-3 px-2 py-1 rounded small fw-semibold"
              style={{ backgroundColor: '#e7f1ff', color: '#0d6efd' }}
              title="Tenant / Parent Group you are signed in to"
            >
              Tenant: {parentGroupLabel}
            </span>
          )}
        </Col>
        <Col className="d-flex justify-content-end">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="d-flex align-items-center"
          >
            {isLoggingOut ? (
              <span>Logging out...</span>
            ) : (
              <>
                <BiLogOut className="me-1" /> Logout
              </>
            )}
          </Button>
        </Col>
      </Row>

      <Row className="mx-0 main-content-row flex-nowrap">
        <Col xs="auto" className="px-0 bg-light border-end sidebar-col">
          <div className="d-flex flex-column align-items-center pt-3 pb-3 h-100">
            <Button
              title="Dashboard"
              className={`mb-2 nav-button nav-button-dashboard ${activeView === 'dashboard' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('dashboard');
                navigate('/dashboard');
              }}
            >
              <BiGridAlt />
              <span className="visually-hidden">Dashboard</span>
            </Button>

            {user?.userGroup === 0 && (
              <Button
                title="User Management"
                className={`mb-2 nav-button nav-button-users ${activeView === 'userManagement' ? 'active' : ''}`}
                onClick={() => {
                  setActiveView('userManagement');
                  navigate('/dashboard/users');
                }}
              >
                <BiUser />
                <span className="visually-hidden">User Management</span>
              </Button>
            )}

            {(user?.userGroup === 0 || user?.userGroup === 1) && (
              <>
                <Button
                  title="Manage Products"
                  className={`mb-2 nav-button nav-button-products ${activeView === 'products' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveView('products');
                    navigate('/dashboard/products');
                  }}
                >
                  <BiShoppingBag />
                  <span className="visually-hidden">Manage Products</span>
                </Button>

                <Button
                  title="Manage Containers"
                  className={`mb-2 nav-button nav-button-containers ${activeView === 'containerTypes' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveView('containerTypes');
                    navigate('/dashboard/container-types');
                  }}
                >
                  <TbBottle />
                  <span className="visually-hidden">Manage Containers</span>
                </Button>
              </>
            )}

            <Button
              title="Inventory"
              className={`mb-2 nav-button nav-button-inventory ${activeView === 'inventory' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('inventory');
                navigate('/dashboard/inventory');
              }}
            >
              <BiPackage />
              <span className="visually-hidden">Inventory</span>
            </Button>

            <button
              type="button"
              title={user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() + ' — Profile' : 'My Profile'}
              className={`profile-avatar-btn mt-auto ${activeView === 'profile' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('profile');
                navigate('/dashboard/profile');
              }}
            >
              <img
                src={profileImageUrl || DEFAULT_AVATAR_URL}
                alt="Profile"
                className="profile-avatar-img"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_AVATAR_URL;
                }}
              />
              <span className="visually-hidden">My Profile</span>
            </button>
          </div>
        </Col>

        <Col className="main-content-area">
          <Outlet />
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;